import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageBody,
  SectionLabel,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import { ScoreBreakdown } from '@/components/admin/recommendations/ScoreBreakdown.tsx';
import { formatScore } from '@/components/admin/recommendations/scoreFormat.ts';
import {
  previewSkillRecommendations,
  type SkillRecommendationPreview,
} from '@/lib/skillRecommendations.ts';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';

export const Route = createFileRoute('/_app/admin/recommendations')({
  staticData: { orgScope: 'none' },
  component: ScorePreviewPage,
});

/* The three bands the Vue page painted: strong, plausible, and everything else. */
function scoreTone(score: number): 'success' | 'warning' | 'quiet' {
  if (score >= 75) return 'success';
  if (score >= 50) return 'warning';
  return 'quiet';
}

/**
 * The recommendation engine run against one user, on demand.
 *
 * It scores but never queues: this is the screen for asking why a skill did or
 * did not come up, without waiting a week for the batch to run again.
 */
export function ScorePreviewPage() {
  const [user, setUser] = useState<UserOption | null>(null);

  const preview = useMutation({
    mutationFn: (userId: string) => previewSkillRecommendations(userId),
  });

  const recommendations: SkillRecommendationPreview[] | undefined = preview.data;

  return (
    <>
      <PageHeader title='Recommendations' />
      <PageBody>
        <ReadingColumn className='flex flex-col gap-5'>
          <Card>
            <div className='flex flex-col gap-4'>
              <div>
                <SectionLabel as='h2'>Skill recommendations</SectionLabel>
                <p className='mt-1 text-sm text-ink-muted'>
                  Recommend skills for a user based on their recent conversations.
                </p>
              </div>

              <Field label='User' htmlFor='preview-user' required>
                <UserPicker
                  id='preview-user'
                  value={user}
                  onChange={setUser}
                  placeholder='Search for a user...'
                  clearable
                />
              </Field>

              <div>
                <Button
                  disabled={user === null}
                  loading={preview.isPending}
                  onClick={() => {
                    if (user) preview.mutate(user.id);
                  }}
                >
                  Get recommendations
                </Button>
              </div>

              {preview.isError && (
                <p role='alert' className='text-sm font-semibold text-danger'>
                  Failed to fetch skill recommendations
                </p>
              )}
            </div>
          </Card>

          {recommendations && recommendations.length === 0 && (
            <Card>
              <EmptyState title='No recommendations for this user' />
            </Card>
          )}

          {recommendations && recommendations.length > 0 && (
            <ul className='flex flex-col gap-3'>
              {recommendations.map((recommendation) => (
                <Card as='li' key={recommendation.skill.id}>
                  <div className='mb-1 flex items-start justify-between gap-3'>
                    <h3 className='text-sm font-semibold text-ink'>
                      {recommendation.skill.name}
                    </h3>
                    <Badge tone={scoreTone(recommendation.final_score)}>
                      {formatScore(recommendation.final_score, 1)}
                    </Badge>
                  </div>
                  <p className='text-sm text-ink-secondary'>{recommendation.reason}</p>
                  <p className='mt-1 text-xs text-ink-muted'>
                    {recommendation.skill.description}
                  </p>
                  <ScoreBreakdown
                    llmScore={recommendation.llm_score}
                    boost={recommendation.boost}
                    freshnessFactor={recommendation.freshness_factor}
                    finalScore={recommendation.final_score}
                    daysSinceLastUsed={recommendation.days_since_last_used}
                  />
                </Card>
              ))}
            </ul>
          )}
        </ReadingColumn>
      </PageBody>
    </>
  );
}
